import { inject, Injectable } from '@angular/core';
import { HisBackendService } from '../../core/service/backend/his-backend.service';
import { Observable, of } from 'rxjs';
import {
    ProblemCaseCreationInfo,
    Recipient,
} from '../model/problem-case-creation-info.model';
import { ProblemCase } from '../model/problem-case.model';
import { SendResult } from '../model/problem-case-send-result.model';
import _ from 'lodash';

@Injectable({providedIn: 'root'})
export class ProblemCaseService {
    private backendService = inject(HisBackendService);

    private apiBase = 'api/v1/cs/psv/problemCase';

    newInstance(
        template: string,
        recipientIds: number[],
        searchEntityIds: number[] | undefined | null,
    ): Observable<ProblemCaseCreationInfo> {
        const theTemplate = this.trimToNull(template);
        const theRecipientIds = this.asCsvStringOrUndefined(recipientIds);
        const theSearchEntityIds = this.asCsvStringOrUndefined(searchEntityIds);

        if (!theTemplate) {
            throw new Error('template is required');
        }

        const baseRequestUrl = `${this.apiBase}/new/${encodeURIComponent(theTemplate)}`;
        const params: { name: string; value: string }[] = [];

        if (theRecipientIds) {
            params.push({ name: 'recipientIds', value: theRecipientIds });
        }
        if (theSearchEntityIds) {
            params.push({ name: 'searchEntityIds', value: theSearchEntityIds });
        }

        if (params.length > 0) {
            const queryString = params
                .map((param) => `${param.name}=${param.value}`)
                .join('&');
            return this.backendService.getData(
                `${baseRequestUrl}?${queryString}`,
            );
        }

        return this.backendService.getData(baseRequestUrl);
    }

    private asCsvStringOrUndefined(values: number[] | undefined | null) {
        if (values && values.length > 0) {
            return values.join(',').trim();
        }
        return undefined;
    }

    isTemplateAvailableForCurrentUser(template: string): Observable<boolean> {
        const theTemplate = this.trimToNull(template);

        if (!theTemplate) {
            throw new Error('template is required');
        }

        return this.backendService.getData(
            `${this.apiBase}/templates/${encodeURIComponent(theTemplate)}/available`,
        );
    }

    send(problemCase: ProblemCase): Observable<SendResult> {
        return this.backendService.postData(
            `${this.apiBase}/send`,
            problemCase,
        );
    }

    findAllRecipients(
        template: string,
        searchEntityIds: number[] | undefined | null,
        autocompleteQuery: string,
    ): Observable<Recipient[]> {
        const theTemplate = this.trimToNull(template);
        const queryParams = this.buildQueryParams(
            searchEntityIds,
            autocompleteQuery,
        );

        const baseRequestUrl = `${this.apiBase}/findRecipientsSet/${encodeURIComponent(theTemplate)}`;

        return this.backendService.getData(`${baseRequestUrl}${queryParams}`);
    }

    private buildQueryParams(
        searchEntityIds: number[] | undefined | null,
        autocompleteQuery: string,
    ): string {
        const queryParams = [];

        if (searchEntityIds && searchEntityIds.length > 0) {
            const theSearchEntityIds =
                this.asCsvStringOrUndefined(searchEntityIds);
            queryParams.push(`searchEntityIds=${theSearchEntityIds}`);
        }

        const q = this.trimToNull(autocompleteQuery);
        if (q) {
            queryParams.push(`q=${encodeURIComponent(q)}`);
        }

        return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    }

    private trimToNull(value: string): string {
        return value?.trim() || null;
    }
}
